// French copy, namespace `auth` (source language: English is typed against it):
// sign-in pages, onboarding, account profile and the public pages reached
// without an account (email preferences, prospecting opt-out).

import type { ReactNode } from "react";

export const auth = {
  pageTitles: {
    login: "Connexion — Hune",
    signup: "Créer un compte — Hune",
    forgotPassword: "Mot de passe oublié — Hune",
    resetPassword: "Nouveau mot de passe — Hune",
    onboarding: "Configurer votre espace — Hune",
    emailPreferences: "Récapitulatif hebdomadaire — Hune",
    stop: "Ne plus recevoir de message — Hune",
  },
  shell: {
    homeLabel: "Hune — accueil",
    legal: {
      terms: "conditions d’utilisation",
      termsOfSale: "conditions de vente",
      privacy: "politique de confidentialité",
      /** The three links are built by the shell, labeled with the strings above. */
      sentence: (terms: ReactNode, termsOfSale: ReactNode, privacy: ReactNode): ReactNode[] => [
        "En continuant, vous acceptez les ",
        terms,
        " et les ",
        termsOfSale,
        " de Hune, et prenez connaissance de sa ",
        privacy,
        ".",
      ],
    },
    testimonial: {
      quote:
        "« Avant Hune, nos commerciaux consacraient des heures à chercher le bon moment pour contacter un compte. Aujourd’hui, ils commencent par les quelques signaux qui méritent vraiment une conversation. »",
      role: "SDR chez AgentifAI",
    },
    sourcesPromise: "Chaque opportunité reste reliée à ses sources.",
    pitch: "Votre veille commerciale, documentée et priorisée par un agent autonome.",
    privacyPromise: "Vos données et vos analyses restent propres à votre espace.",
  },
  form: {
    modes: {
      login: {
        title: "Connectez-vous",
        submit: "Se connecter",
      },
      signup: {
        title: "Créez votre compte",
        submit: "Créer mon compte",
      },
      forgot: {
        eyebrow: "Récupération",
        title: "Mot de passe oublié ?",
        description: "Nous vous envoyons un lien sécurisé pour choisir un nouveau mot de passe.",
        submit: "Recevoir le lien",
      },
      reset: {
        eyebrow: "Nouveau mot de passe",
        title: "Sécurisez votre compte",
        description: "Choisissez un mot de passe d’au moins huit caractères.",
        submit: "Mettre à jour",
      },
    },
    /** Before the site name, set in bold by the form. */
    firstAnalysisOn: "Première analyse prévue sur",
    google: "Continuer avec Google",
    orWithEmail: "ou avec votre e-mail",
    email: "Adresse e-mail",
    emailPlaceholder: "vous@entreprise.fr",
    password: "Mot de passe",
    newPassword: "Nouveau mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    forgotLink: "Mot de passe oublié ?",
    busy: "Un instant…",
    passwordTooShort: (min: number) => `Le mot de passe doit contenir au moins ${min} caractères.`,
    passwordsDiffer: "Les deux mots de passe ne correspondent pas.",
    resetSent: "Si cette adresse existe, un lien de réinitialisation vient d’être envoyé.",
    passwordUpdated: "Mot de passe mis à jour. Redirection vers votre espace…",
    failed: "Une erreur est survenue.",
    noAccount: "Pas encore de compte ?",
    signupLink: "Créer un compte",
    hasAccount: "Déjà inscrit ?",
    loginLink: "Se connecter",
    backToLogin: "Retour à la connexion",
  },
  resetPassword: {
    invalidLink: "Le lien est invalide ou a expiré. Demandez-en un nouveau.",
    subject: "cette page",
  },
  onboarding: {
    steps: {
      label: "Étapes",
      you: "Vous",
      site: "Votre site",
      last: "Dernière question",
    },
    /** Suggested roles, keyed by a stable id; the chosen label is saved as typed text. */
    roles: {
      founder: "Fondateur ou dirigeant",
      sales: "Commercial",
      marketing: "Marketing",
      agency: "Agence ou consultant",
      other: "Autre",
    },
    /** Acquisition sources, keyed by a stable id; the French label is what the account stores. */
    sources: {
      google: "Recherche Google",
      linkedin: "LinkedIn",
      referral: "Un ami ou un collègue",
      email: "Un e-mail de Hune",
      content: "Un article ou un podcast",
      event: "Un évènement",
      ad: "Une publicité",
      other: "Autre",
    },
    switchAccount: "Changer de compte",
    stepCounter: (step: number, total: number) => `${step} / ${total}`,
    titles: {
      welcome: "Bienvenue",
      welcomeName: (firstName: string) => `Bienvenue, ${firstName}`,
      welcomeLedePrefilled:
        "Vérifiez ces informations : elles servent à votre espace et à nos échanges. Il ne reste que deux étapes ensuite.",
      welcomeLede: "Quelques informations pour votre espace et nos échanges. Il ne restera que deux étapes ensuite.",
      site: "Que vendez-vous ?",
      siteLede:
        "L’adresse de la page qui présente votre produit ou votre service. C’est tout ce dont l’agent a besoin pour comprendre votre marché.",
      source: "Comment avez-vous connu Hune ?",
      sourceLede: "Facultatif. Votre réponse nous dit où continuer à parler de Hune.",
    },
    fields: {
      firstName: "Prénom",
      lastName: "Nom",
      prefilled: "Repris de votre compte",
      company: "Entreprise",
      role: "Votre fonction",
      optional: "— facultatif",
      otherRoleLabel: "Précisez votre fonction",
      otherRolePlaceholder: "Précisez : responsable partenariats, office manager…",
      website: "Adresse de votre site",
      websitePlaceholder: "votre-site.fr",
      websiteHint: "La page d’un produit précis donne une cible plus nette que la page d’accueil.",
      websiteUnreachable: "Le site ne répond pas : vérifiez l’adresse. Vous pouvez continuer si elle est juste.",
      sourceLabel: "Comment avez-vous connu Hune ?",
    },
    siteStatus: {
      checking: "Vérification",
      ok: "Page trouvée",
      unreachable: "Introuvable",
    },
    errors: {
      invalidSite: "Saisissez l’adresse d’un site public, par exemple votre-site.fr.",
      saveFailed: "Impossible d’enregistrer votre profil.",
    },
    actions: {
      back: "Retour",
      continue: "Continuer",
      launch: "Lancer ma première analyse",
      launching: "Lancement de votre analyse…",
      /** Before the key cap. */
      or: "ou",
      enterKey: "Entrée ↵",
      launchAnyway: "Sans réponse, le bouton lance l’analyse quand même.",
    },
    agentPlan: {
      title: "Ce que fera l’agent",
      timing: "Une dizaine de minutes après la dernière étape.",
      readsSite: (site: string) => `Il lit ${site}`,
      readsYourSite: "Il lit votre site",
      readsSiteBody: "Ce que vous vendez, à qui, et ce qui vous distingue.",
      target: "Il en déduit votre cible",
      targetBody: "Le segment prioritaire, le décideur, les moments où le besoin devient urgent.",
      signals: "Il cherche des signaux",
      signalsBody: "Presse, offres d’emploi, appels d’offres, registres, LinkedIn.",
      leads: "Vos premiers leads arrivent",
      leadsBody: "Datés, sourcés, avec les bons interlocuteurs. Vous corrigez la cible quand vous voulez.",
    },
    summary: {
      title: "Prêt à lancer",
      editable: "Tout reste modifiable ensuite.",
      you: "Vous",
      company: "Entreprise",
      site: "Site analysé",
      edit: "Modifier",
      /** Screen-reader suffix of the edit button: « Modifier : entreprise ». */
      editTarget: (term: string) => ` : ${term.toLowerCase()}`,
      noContact: "Hune ne contacte personne à votre place.",
    },
  },
  account: {
    firstName: "Prénom",
    lastName: "Nom",
    email: "E-mail",
    company: "Entreprise",
    role: "Fonction",
    website: "Site du produit",
    save: "Enregistrer",
    saved: "Enregistré",
    changePassword: "Changer le mot de passe",
  },
  emailPreferences: {
    title: "Ne plus recevoir le récapitulatif hebdomadaire",
    done: "C’est fait.",
    doneBody:
      "Vous ne recevrez plus le récapitulatif hebdomadaire. La veille continue : les nouveaux leads restent dans votre pipeline. Vous pouvez le réactiver à tout moment dans vos paramètres, rubrique « Notifications ».",
    body: "Le récapitulatif part une fois par semaine au plus, et seulement quand l’agent a trouvé du nouveau sur vos offres. Un clic suffit pour le couper ; la veille, elle, continue.",
    submit: "Ne plus le recevoir",
    manage: "Gérer vos e-mails dans les paramètres",
    privacy: "Politique de confidentialité",
  },
  stop: {
    title: "Ne plus recevoir de message de Hune",
    done: "C’est fait.",
    doneBody: (brand: string) =>
      `Votre adresse ne recevra plus aucun e-mail de prospection de ${brand}, relances comprises. Cette décision est définitive : seule une empreinte de votre adresse est conservée, pour la respecter.`,
    body: (brand: string) =>
      `Un clic suffit : votre adresse sera retirée de la prospection de ${brand}, définitivement. Vous pouvez aussi répondre « stop » à n’importe lequel de nos messages.`,
    submit: "Ne plus recevoir de message",
    question: "Une question sur vos données :",
    privacy: "Politique de confidentialité",
  },
  tourWelcome: {
    video: "Hune en vidéo",
    exploring: "L’agent explore votre marché",
    /** Pages read so far (none yet: only the expected duration). */
    progress: (pages: number | null) => {
      const duration = "une dizaine de minutes au total";
      if (!pages) return duration;
      return `${pages > 1 ? `${pages} pages lues` : `${pages} page lue`} · ${duration}`;
    },
    ideas: {
      searches: { title: "Il cherche", body: "Des signaux datés et sourcés, sur votre marché." },
      contact: { title: "Vous contactez", body: "Un message prêt par interlocuteur, envoyé par vous." },
      continues: { title: "Il continue", body: "La veille repasse seule et ne montre que le nouveau." },
    },
    start: "Découvrir mon espace",
  },
};
